import React, { useState } from "react";
import { Table, Typography, Tag, Popover } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { map } from "underscore";
import { IoMdCloseCircle } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";
import { startCase } from "@ts/utils";
import { IReviewsAndVerbatimTableWidget, IReviewsAndVerbatimTableEntry, TProductReviewTableExpandedRow, TProductReviewExpandedTableDataEntry } from "@ts/common-types";

const { Text } = Typography;

const ReviewsAndVerbatimTableWidget: React.FC<IReviewsAndVerbatimTableWidget> = ({ widgetData }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const handleExpand = (expanded: boolean, record: IReviewsAndVerbatimTableEntry) => {
        setExpandedRowKeys(expanded ? [record.key] : []);
    };

    const columns = [
        {
            title: "Review Id",
            dataIndex: "review_id",
            render: (review_id: string) => (
                <Text>{`${review_id.slice(0, 8)}...`}</Text>
            ),
        },
        { title: "Title", dataIndex: "review_title" },
        {
            title: "Review Content",
            dataIndex: "content",
            width: 200,
            render: (content: string) => (
                <Popover content={<div className="p-2">{content}</div>}>
                    <Text
                        className="text-truncate overflow-hidden white-space-nowrap d-block"
                        title={content}
                    >
                        {content.length > 50
                            ? `${content.slice(0, 50)}...`
                            : content}
                    </Text>
                </Popover>
            ),
        },
        { title: "Channel", dataIndex: "channel" },
        { title: "Sentiment Score", dataIndex: "sentiment_score" },
        { title: "Date Published", dataIndex: "review_date" },
        {
            title: "Found Helpful",
            dataIndex: "found_helpful",
            render: (found_helpful: boolean) => (
                <Text
                    className={`text-${found_helpful ? "success" : "danger"}`}
                >
                    {found_helpful ? "Found Helpful" : "Not Helpful"}
                </Text>
            ),
        },
        {
            title: "Rating",
            dataIndex: "rating",
        },
        {
            title: "About Driver",
            dataIndex: "about_driver",
            width: 250,
            render: (drivers: Record<string, string>[]) => (
                <Popover
                    content={
                        <div className="d-flex flex-start align-items-center p-2">
                            {map(drivers, (driver, index) => (
                                <Tag
                                    key={index}
                                    icon={
                                        driver.type === "positive" ? (
                                            <FaCheckCircle className="text-success me-1" />
                                        ) : (
                                            <IoMdCloseCircle className="text-danger me-1" />
                                        )
                                    }
                                >
                                    {startCase(driver.name)}
                                </Tag>
                            ))}
                        </div>
                    }
                    trigger="hover"
                    overlayStyle={{ maxWidth: 400 }}
                >
                    <div
                        className="d-flex flex-start align-items-center white-space-nowrap text-truncate"
                        style={{ maxWidth: 200 }}
                    >
                        {map(drivers.slice(0, 2), (driver, index) => (
                            <Tag
                                key={index}
                                className="me-1 overflow-hidden text-truncate"
                                icon={
                                    driver.type === "positive" ? (
                                        <FaCheckCircle className="text-success me-1" />
                                    ) : (
                                        <IoMdCloseCircle className="text-danger me-1" />
                                    )
                                }
                                style={{ maxWidth: 90 }}
                            >
                                {startCase(driver.name)}
                            </Tag>
                        ))}
                        {drivers.length > 3 && (
                            <span
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                            >
                                +{drivers.length - 2}
                            </span>
                        )}
                    </div>
                </Popover>
            ),
        },
    ];


    const expandedRowRender = (record: TProductReviewTableExpandedRow) => {
        const detailsColumns = [
            { title: "Verbatim", dataIndex: "verbatim" },
            { title: "Sentiment", dataIndex: "sentiment_type" },
            { title: "Sentiment Score", dataIndex: "sentiment_score" },
            { title: "Date", dataIndex: "review_date" },
            { title: "Theme", dataIndex: "theme" },
        ];

        return (
            <Table
                columns={detailsColumns}
                dataSource={record.details}
                pagination={false}
                size="small"
                rowKey={(row: TProductReviewExpandedTableDataEntry) => row.verbatim}
            />
        );
    };

    return (
        <Table
            columns={columns}
            dataSource={widgetData}
            expandable={{
                expandIcon: ({ expanded, onExpand, record }) =>
                    expanded ? (
                        <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
                    ) : (
                        <CaretDownOutlined
                            onClick={(e) => onExpand(record, e)}
                        />
                    ),
                expandedRowRender,
                expandedRowKeys,
                onExpand: handleExpand,
            }}
            rowKey="key"
            pagination={false}
            scroll={{ y: 400 }}
            size="small"
        />
    );
};

export default ReviewsAndVerbatimTableWidget;